import { useState } from 'react';
import { cn } from '@/lib/utils';
import { Globe, Image } from 'lucide-react';
import { getPlatformFromUrl, platformConfig } from './SocialPlatformBadge';

interface SourceIconProps {
  url?: string | null;
  sourceType?: string;
  size?: 'sm' | 'md';
  className?: string;
}

const getDomainFromUrl = (url: string): string | null => {
  try {
    return new URL(url).hostname.replace('www.', '');
  } catch {
    return null;
  }
};

// Use icon.horse which returns proper 404s for missing favicons
const getFaviconUrl = (domain: string): string => 
  `https://icon.horse/icon/${domain}`;

export function SourceIcon({ url, sourceType, size = 'sm', className }: SourceIconProps) {
  const [faviconError, setFaviconError] = useState(false);
  
  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
  };
  
  // Handle image uploads
  if (sourceType === 'image') {
    return (
      <Image 
        className={cn(sizeClasses[size], 'text-current', className)} 
        strokeWidth={1.5}
      />
    );
  }
  
  // Check if it's a social platform
  if (url) {
    const platform = getPlatformFromUrl(url);
    if (platform && platformConfig[platform]) {
      const config = platformConfig[platform];
      if (config.logo) {
        return (
          <img 
            src={config.logo} 
            alt={config.label}
            className={cn(sizeClasses[size], 'object-contain', className)}
          />
        );
      }
    }
    
    // Try to get favicon for regular URLs
    const domain = getDomainFromUrl(url);
    if (domain && !faviconError) {
      return (
        <img 
          src={getFaviconUrl(domain)} 
          alt={domain}
          className={cn(sizeClasses[size], 'object-contain rounded-sm', className)}
          onError={() => setFaviconError(true)}
        />
      );
    }
  }
  
  // Fallback to globe icon
  return (
    <Globe 
      className={cn(sizeClasses[size], 'text-current', className)} 
      strokeWidth={1.5}
    />
  );
}
